import SparkMD5 from "spark-md5";

import { uploadDirect } from "@/api";

function readArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });
}

function md5Base64(buffer: ArrayBuffer): string {
  const spark = new SparkMD5.ArrayBuffer();
  spark.append(buffer);
  return btoa(spark.end(true));
}

export type UploadFileOptions = {
  signal?: AbortSignal;
  onProgress?: (progress: number) => void;
};

function putWithProgress(
  url: string,
  headers: Record<string, string>,
  body: ArrayBuffer,
  { signal, onProgress }: UploadFileOptions,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    for (const [name, value] of Object.entries(headers)) {
      xhr.setRequestHeader(name, value);
    }

    const onAbort = () => xhr.abort();
    const cleanup = () => signal?.removeEventListener("abort", onAbort);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.(event.loaded / event.total);
      }
    };

    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve();
      } else {
        reject(new Error(`storage responded with ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => {
      cleanup();
      reject(new Error("network error"));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", onAbort);
    }

    xhr.send(body);
  });
}

export async function uploadFile(file: File, options: UploadFileOptions = {}): Promise<string> {
  const { signal } = options;
  const buffer = await readArrayBuffer(file);

  const { key, url, headers } = await uploadDirect(
    {
      blob: {
        filename: file.name,
        byteSize: file.size,
        checksum: md5Base64(buffer),
        contentType: file.type || "application/octet-stream",
      },
    },
    { signal },
  );

  try {
    await putWithProgress(url, headers, buffer, options);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new Error(`Failed to upload file "${file.name}" to storage: ${(err as Error).message}`);
  }

  return key;
}
